// src/pages/Login.js
import React, { useState, useRef } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { supabase } from '../config/supabase.config';
import './Login.css';

function Login({ onLoginSuccess, onSwitchToRegister, onSwitchToForgotPassword }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  const emailRef = useRef();
  const passwordRef = useRef();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    const email = emailRef.current?.value;
    const password = passwordRef.current?.value;
    
    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }
    
    setLoading(true);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const { data: userData, error: supabaseError } = await supabase
        .from('residents')
        .select('*')
        .eq('firebase_uid', firebaseUser.uid)
        .single();
      
      if (supabaseError || !userData) {
        setError('User not found in database');
        setLoading(false);
        return;
      }
      
      if (userData.status === 'pending') {
        setError('Your account is pending approval');
        await auth.signOut();
        setLoading(false);
        return;
      }
      
      if (userData.account_status === 'inactive') {
        setError('Your account is inactive. Please contact admin');
        await auth.signOut();
        setLoading(false);
        return;
      }
      
      onLoginSuccess(firebaseUser, userData);
      
    } catch (error) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else {
        setError('Invalid credentials');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-split-container">
        {/* Left Panel - Branding */}
        <div className="login-left-panel">
          <div className="branding-content">
            <div className="brand-header">
              <div className="brand-icon">🏢</div>
              <h1>Srinivasa Flats</h1>
              <p>Community Management System</p>
            </div>
            
            <div className="features-list">
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>Digital Payment Management</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>Meeting Scheduling & Minutes</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>Maintenance Tracking</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>Service Directory</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>Complaint Management</span>
              </div>
            </div>
            
            <div className="brand-footer">
              <p>Powered by <strong>STRONARIX</strong></p>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Login Form */}
        <div className="login-right-panel">
          <div className="login-form-container">
            <div className="form-header">
              <h2>Welcome Back</h2>
              <p>Sign in to your account to continue</p>
            </div>
            
            <form className="login-form" onSubmit={handleLogin}>
              {error && (
                <div className="alert-error">
                  {error}
                </div>
              )}
              
              <div className="form-group">
                <label>Email Address</label>
                <input
                  ref={emailRef}
                  type="email"
                  className="form-control"
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Password</label>
                <input
                  ref={passwordRef}
                  type="password"
                  className="form-control"
                  placeholder="Enter your password"
                  required
                />
              </div>
              
              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  onSwitchToForgotPassword();
                }} className="forgot-link">
                  Forgot password?
                </a>
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Signing in...' : 'SIGN IN'}
              </button>
              
              <button type="button" className="btn-secondary" onClick={onSwitchToRegister}>
                Create New Account
              </button>
            </form>
            
            <div className="terms-footer">
              <p>By signing in, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;